import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Manufacturing Module Service Stores
 */
export const manufacturingStores: ServiceStoreConfig[] = [
  {
    name: "bill-of-materials",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.billOfMaterialsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalBOMs = entities.length;
          const activeBOMs = entities.filter(
            (bom: Record<string, unknown>) =>
              bom.status === "active" || !bom.status,
          ).length;
          const draftBOMs = entities.filter(
            (bom: Record<string, unknown>) => bom.status === "draft",
          ).length;
          const recentBOMs = entities.filter((bom: Record<string, unknown>) => {
            const createdDate = new Date(bom.createdAt as string);
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            return createdDate > sevenDaysAgo;
          }).length;

          return [
            {
              title: "Total BOMs",
              value: () => totalBOMs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeBOMs.toString(),
              valueColor: "success.main",
            },
            {
              title: "Drafts",
              value: () => draftBOMs.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Recent (7 days)",
              value: () => recentBOMs.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "bom-extraction",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.bomExtractionStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalExtractions = entities.length;
          const completedExtractions = entities.filter(
            (extraction: Record<string, unknown>) =>
              extraction.processingStatus === "completed",
          ).length;
          const pendingExtractions = entities.filter(
            (extraction: Record<string, unknown>) =>
              extraction.processingStatus === "pending" ||
              extraction.processingStatus === "processing",
          ).length;
          const failedExtractions = entities.filter(
            (extraction: Record<string, unknown>) =>
              extraction.processingStatus === "failed",
          ).length;

          return [
            {
              title: "Total Extractions",
              value: () => totalExtractions.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Completed",
              value: () => completedExtractions.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pendingExtractions.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Failed",
              value: () => failedExtractions.toString(),
              valueColor: "error.main",
            },
          ];
        },
      },
    },
  },
  {
    name: "bom-extraction-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  {
    name: "bom-items",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  {
    name: "hold",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.holdStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalHolds = entities.length;
          const activeHolds = entities.filter(
            (hold: Record<string, unknown>) =>
              hold.status === "active" || !hold.status,
          ).length;
          const resolvedHolds = entities.filter(
            (hold: Record<string, unknown>) => hold.status === "resolved",
          ).length;
          const criticalHolds = entities.filter(
            (hold: Record<string, unknown>) =>
              hold.priority === "critical" || hold.severity === "high",
          ).length;

          return [
            {
              title: "Total Holds",
              value: () => totalHolds.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeHolds.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Resolved",
              value: () => resolvedHolds.toString(),
              valueColor: "success.main",
            },
            {
              title: "Critical",
              value: () => criticalHolds.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "qc",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.qcStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalInspections = entities.length;
          const passedInspections = entities.filter(
            (qc: Record<string, unknown>) => qc.status === "passed",
          ).length;
          const failedInspections = entities.filter(
            (qc: Record<string, unknown>) => qc.status === "failed",
          ).length;
          const pendingInspections = entities.filter(
            (qc: Record<string, unknown>) =>
              qc.status === "pending" || !qc.status,
          ).length;

          return [
            {
              title: "Total Inspections",
              value: () => totalInspections.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Passed",
              value: () => passedInspections.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failedInspections.toString(),
              valueColor: "error.main",
            },
            {
              title: "Pending",
              value: () => pendingInspections.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  // Materials Management Services
  {
    name: "materials",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          return [
            {
              title: "Total Materials",
              value: () => {
                const materialsStore = rootStore.materialsStore as {
                  entities?: unknown[];
                };
                const entities = materialsStore?.entities || [];
                return entities.length.toString();
              },
              valueColor: "primary.main",
            },
            {
              title: "Active Items",
              value: () => {
                const materialsStore = rootStore.materialsStore as {
                  entities?: unknown[];
                };
                const entities = materialsStore?.entities || [];
                const activeItems = entities.filter(
                  (material: Record<string, unknown>) =>
                    material.status === "active" || !material.status,
                ).length;
                return activeItems.toString();
              },
              valueColor: "success.main",
            },
            {
              title: "Low Stock",
              value: () => {
                const materialsStore = rootStore.materialsStore as {
                  entities?: unknown[];
                };
                const entities = materialsStore?.entities || [];
                const lowStock = entities.filter(
                  (material: Record<string, unknown>) => {
                    const qty = (material.quantity as number) || 0;
                    const min = (material.minQuantity as number) || 0;
                    return qty <= min && min > 0;
                  },
                ).length;
                return lowStock.toString();
              },
              valueColor: "error.main",
            },
            {
              title: "Total Value",
              value: () => {
                const materialsStore = rootStore.materialsStore as {
                  entities?: unknown[];
                };
                const entities = materialsStore?.entities || [];
                const totalValue = entities.reduce(
                  (sum: number, material: Record<string, unknown>) => {
                    const qty = (material.quantity as number) || 0;
                    const price = (material.unitPrice as number) || 0;
                    return sum + qty * price;
                  },
                  0,
                );
                return `$${(totalValue as number).toFixed(2)}`;
              },
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["name"],
          sortDirection: { name: "ASC" },
        },
      },
    },
  },
  {
    name: "material-inventory",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  {
    name: "material-transactions",
    options: {
      enableStorage: false, // Transactions should always be fresh
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  {
    name: "material-vendors",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  {
    name: "material-locations",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  {
    name: "material-reorder-rules",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  {
    name: "material-quality-certs",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  // Stack Designer module stores

  {
    name: "casting-map-layout",
    options: { enableStorage: false, enableErrorNotifications: false },
  },
  {
    name: "cost-code",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  {
    name: "ifc-status",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  {
    name: "mold-engineer-assignments",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  // Product Management module stores
  {
    name: "product-categories",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  {
    name: "product-variants",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  {
    name: "product-pricing",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  {
    name: "product-inventory",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  {
    name: "products",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.productsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalProducts = entities.length;
          const activeProducts = entities.filter(
            (product: Record<string, unknown>) =>
              product.status === "active" || product.isActive,
          ).length;
          const outOfStock = entities.filter(
            (product: Record<string, unknown>) =>
              (product.stockQuantity as number) === 0 || product.outOfStock,
          ).length;
          const categories = new Set(
            entities
              .map((product: Record<string, unknown>) => product.category)
              .filter(Boolean),
          ).size;

          return [
            {
              title: "Total Products",
              value: () => totalProducts.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeProducts.toString(),
              valueColor: "success.main",
            },
            {
              title: "Out of Stock",
              value: () => outOfStock.toString(),
              valueColor: "error.main",
            },
            {
              title: "Categories",
              value: () => categories.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  // New backend service - NCR
  {
    name: "ncr",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
  {
    name: "ncr-attachments",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "id",
    },
  },
];
